<script setup>
// Cinematic radio-style payment method card — gold-rail accent when selected.

const props = defineProps({
  methodId:  { type: String, required: true },
  label:     { type: String, required: true },
  sublabel:  { type: String, default: '' },
  logos:     { type: Array, default: () => [] },
  disabled:  { type: Boolean, default: false },
  selected:  { type: Boolean, default: false }
})

const emit = defineEmits(['select'])

function choose() {
  if (!props.disabled) emit('select', props.methodId)
}
</script>

<template>
  <button
    type="button"
    class="stamp"
    :class="{ 'stamp--on': selected, 'stamp--off': disabled }"
    :aria-pressed="selected"
    :disabled="disabled"
    @click="choose"
  >
    <!-- Radio dot -->
    <span class="stamp__radio" :class="{ 'stamp__radio--filled': selected }" aria-hidden="true" />

    <span class="stamp__body">
      <span class="stamp__label u-display u-allcaps">{{ label }}</span>
      <span v-if="sublabel" class="stamp__sub u-mono">{{ sublabel }}</span>
    </span>

    <span v-if="logos && logos.length" class="stamp__logos" aria-hidden="true">
      <img v-for="(l, i) in logos" :key="i" :src="l" :alt="label" class="stamp__logo" />
    </span>
  </button>
</template>

<style scoped>
.stamp {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  width: 100%;
  padding: var(--sp-3) var(--sp-4);
  border-radius: var(--r-md);
  border: 1px solid var(--ash);
  background: var(--bg-glass);
  text-align: left;
  cursor: pointer;
  transition:
    background    var(--t-fast) var(--ease-road),
    border-color  var(--t-fast) var(--ease-road),
    box-shadow    var(--t-fast) var(--ease-road);
}

.stamp:hover:not(.stamp--off) {
  border-color: var(--ash-strong);
  background: var(--bg-glass-strong);
}

.stamp--on {
  border-color: var(--ash);
  background: rgba(229, 199, 107, 0.06);
  box-shadow: inset 2px 0 0 var(--gold);
}

.stamp--off {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Radio */
.stamp__radio {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid var(--ash-strong);
  background: transparent;
  position: relative;
  transition: border-color var(--t-fast) var(--ease-road);
}

.stamp__radio--filled {
  border-color: var(--gold);
}

.stamp__radio::after {
  content: '';
  position: absolute;
  inset: 3px;
  border-radius: 50%;
  background: var(--gold);
  opacity: 0;
  transition: opacity var(--t-fast) var(--ease-road);
}

.stamp__radio--filled::after {
  opacity: 1;
}

/* Text */
.stamp__body {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* Logos — pushed to the right edge */
.stamp__logos {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
  flex-shrink: 0;
}
.stamp__logo {
  height: 20px;
  width: auto;
  /* Match the footer treatment: no white plate — transparent, slightly dimmed +
     grayscale, full colour on hover/selection. */
  filter: grayscale(60%) brightness(0.85);
  opacity: 0.85;
  transition: filter var(--t-fast) var(--ease-road), opacity var(--t-fast) var(--ease-road);
}
.stamp:hover .stamp__logo,
.stamp--on .stamp__logo {
  filter: grayscale(0%) brightness(1);
  opacity: 1;
}

.stamp__label {
  font-size: var(--fs-14);
  color: var(--ink);
  line-height: var(--lh-snug);
}

.stamp__sub {
  font-size: var(--fs-12);
  color: var(--gold);
  opacity: 0.8;
  line-height: 1;
}
</style>
